import React from 'react';
import { Box, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { UserRole } from '@/types/user';
import { RootState } from '@/store/store';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useGetTimeBasedTransactionsQuery } from '@/services/dashboardApi';

interface Props {
    date: string; // YYYY-MM-DD
    interval?: number; // hours
    amcId?: string;
}

export const TimeBasedTransactionsChart: React.FC<Props> = ({ date, interval = 1, amcId }) => {
    const theme = useTheme();
    const role = useSelector((state: RootState) => state.user.role) as UserRole | undefined;
    const isAuthorized = role === UserRole.AMC_ADMIN || role === UserRole.AMC;
    if (!isAuthorized) return null;
    
    // Build API parameters with optional AMC filter
    const apiParams: any = { date, interval };
    if (amcId) {
        apiParams.amcId = amcId;
    }
    
    const { data } = useGetTimeBasedTransactionsQuery(apiParams);

    const rows = Array.isArray(data) ? data : [];
    const categories = rows.map((r) => r.timeSlot);

    const options: Highcharts.Options = {
        credits: { enabled: false },
        chart: { type: 'line' },
        title: {
            text: 'Transactions Over Time (24Hours)',
            align: 'left',
            style: {
                ...theme.typography.h3,
                fontWeight: theme.typography.h3.fontWeight?.toString(),
            },
        },
        xAxis: {
            categories,
            lineColor: theme.palette.divider,
        },
        yAxis: {
            title: { text: '' },
            gridLineDashStyle: 'Dash',
            gridLineColor: theme.palette.divider,
        },
        legend: {
            enabled: true,
            align: 'left',
            verticalAlign: 'top',
            itemStyle: {
                color: theme.typography.subtitle1.color,
                fontSize: theme.typography.subtitle1.fontSize?.toString(),
                fontWeight: theme.typography.subtitle1.fontWeight?.toString(),
                fontFamily: 'Roboto',
            },
            symbolRadius: 50,
            symbolHeight: 10,
            symbolWidth: 10,
            itemDistance: 30,
            itemMarginBottom: 5,
        },
        plotOptions: {
            line: {
                marker: { enabled: true, radius: 4 },
            },
        },
        series: [
            {
                type: 'line',
                name: 'Lumpsum',
                data: rows.map((r) => r.lumpsumCount || 0),
                color: theme.palette.success.main,
                marker: { symbol: 'circle' },
            },
            {
                type: 'line',
                name: 'Mandate',
                data: rows.map((r) => r.mandateCount || 0),
                color: theme.palette.info.main,
                marker: { symbol: 'triangle' },
            },
        ],
    };

    return (
        <Box
            sx={{
                backgroundColor: 'white',
                borderRadius: '10px',
                p: 3,
                flex: 1,
                minWidth: '300px',
            }}
        >
            <HighchartsReact highcharts={Highcharts} options={options} />
        </Box>
    );
};


